import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Receipt as InvoiceIcon,
  LocalShipping as DeliveryIcon,
  CheckCircle as ConfirmIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import { salesOrderService } from '../../../services/salesOrderService';
import { SalesOrder, SalesOrderStatus, SALES_ORDER_STATUS_COLORS } from '../../../types/salesOrder';
import { formatCurrency } from '../../../utils/formatters';
import DocumentAuditHistory from '../../../components/audit/DocumentAuditHistory';
import { DocumentContextMenu } from '../../../components/documents/DocumentContextMenu';
import SalesOrderPDFPreview from '../../../components/sales-orders/SalesOrderPDFPreview';
import ShipmentSection from '../../../components/sales-orders/ShipmentSection';

const SalesOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [salesOrder, setSalesOrder] = useState<SalesOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [convertToInvoiceOpen, setConvertToInvoiceOpen] = useState(false);
  const [dueDays, setDueDays] = useState(30);
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<SalesOrderStatus>(SalesOrderStatus.CONFIRMED);
  const [statusNotes, setStatusNotes] = useState('');

  // Fetch sales order data
  useEffect(() => {
    const fetchSalesOrder = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await salesOrderService.getSalesOrder(id);
        setSalesOrder(data);
      } catch (error: any) {
        console.error('Failed to fetch sales order:', error);
        setError(error?.response?.data?.detail || 'Failed to load sales order');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesOrder();
  }, [id]);

  const getStatusColor = useCallback((status: SalesOrderStatus) => {
    return SALES_ORDER_STATUS_COLORS[status] || 'default';
  }, []);

  const permissions = useMemo(() => {
    if (!salesOrder) return {
      canEdit: false,
      canConfirm: false,
      canProcess: false,
      canReadyToShip: false,
      canShip: false,
      canCancel: false,
      canConvertToInvoice: false,
      canCreateDeliveryChallan: false
    };

    return {
      canEdit: salesOrder.status === SalesOrderStatus.DRAFT,
      canConfirm: salesOrder.status === SalesOrderStatus.DRAFT,
      canProcess: salesOrder.status === SalesOrderStatus.CONFIRMED,
      canReadyToShip: salesOrder.status === SalesOrderStatus.PROCESSING,
      canShip: salesOrder.status === SalesOrderStatus.READY_TO_SHIP,
      canCancel: ![SalesOrderStatus.CANCELLED, SalesOrderStatus.INVOICED].includes(salesOrder.status),
      canConvertToInvoice: [
        SalesOrderStatus.CONFIRMED,
        SalesOrderStatus.PROCESSING,
        SalesOrderStatus.READY_TO_SHIP,
        SalesOrderStatus.SHIPPED
      ].includes(salesOrder.status),
      canCreateDeliveryChallan: [
        SalesOrderStatus.CONFIRMED,
        SalesOrderStatus.PROCESSING,
        SalesOrderStatus.READY_TO_SHIP
      ].includes(salesOrder.status)
    };
  }, [salesOrder?.status]);

  // Valid status transitions based on current status
  const validStatusTransitions = useMemo(() => {
    if (!salesOrder) return [];
    
    const transitions: Record<SalesOrderStatus, SalesOrderStatus[]> = {
      [SalesOrderStatus.DRAFT]: [SalesOrderStatus.CONFIRMED, SalesOrderStatus.CANCELLED],
      [SalesOrderStatus.CONFIRMED]: [SalesOrderStatus.PROCESSING, SalesOrderStatus.CANCELLED],
      [SalesOrderStatus.PROCESSING]: [SalesOrderStatus.READY_TO_SHIP, SalesOrderStatus.CANCELLED],
      [SalesOrderStatus.READY_TO_SHIP]: [SalesOrderStatus.SHIPPED],
      [SalesOrderStatus.SHIPPED]: [SalesOrderStatus.DELIVERED],
      [SalesOrderStatus.DELIVERED]: [],
      [SalesOrderStatus.INVOICED]: [],
      [SalesOrderStatus.CANCELLED]: [],
    };
    
    return transitions[salesOrder.status] || [];
  }, [salesOrder?.status]);

  const handleEdit = () => {
    navigate(`/admin/documents/sales-orders/${id}/edit`);
  };

  const handleDownloadPDF = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const blob = await salesOrderService.downloadPDF(salesOrder.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${salesOrder.order_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to download PDF:', error);
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.confirmSalesOrder(salesOrder.id);
      setSalesOrder(updatedOrder);
      enqueueSnackbar('Sales order confirmed successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to confirm sales order:', error);
      
      // Handle insufficient stock error with details
      if (error.response?.data?.detail?.error === 'INSUFFICIENT_STOCK') {
        const insufficientItems = error.response.data.detail.insufficient_items || [];
        
        if (insufficientItems.length > 0) {
          // Create a user-friendly message
          const itemsList = insufficientItems
            .map((item: any) => {
              const productName = item.product_name || 'Unknown Product';
              const variantName = item.variant_name || item.sku || 'N/A';
              const required = item.required_quantity || item.requested_quantity || 0;
              const available = item.available_quantity || 0;
              const short = item.shortfall || (required - available);
              
              return `• ${productName} (${variantName}): Need ${required} units, only ${available} available (short by ${short})`;
            })
            .join('\n');
          
          enqueueSnackbar(
            `Cannot confirm order due to insufficient stock:\n\n${itemsList}\n\nPlease adjust quantities or wait for stock replenishment.`,
            { 
              variant: 'error',
              autoHideDuration: 15000,
              style: { whiteSpace: 'pre-line', maxWidth: '600px' }
            }
          );
        } else {
          enqueueSnackbar('Cannot confirm order - Insufficient stock for one or more items', { variant: 'error' });
        }
      } else if (error.response?.data?.detail?.code === 'INVALID_STATUS_TRANSITION') {
        const detail = error.response.data.detail;
        enqueueSnackbar(
          `Cannot change status: ${detail.message || 'Invalid status transition'}`,
          { variant: 'error' }
        );
      } else if (error.response?.data?.detail?.message) {
        enqueueSnackbar(error.response.data.detail.message, { variant: 'error' });
      } else if (error.response?.data?.detail) {
        const detail = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
        enqueueSnackbar(detail, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to confirm sales order. Please try again.', { variant: 'error' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleProcess = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.updateStatus(salesOrder.id, {
        status: SalesOrderStatus.PROCESSING,
        notes: 'Order moved to processing'
      });
      setSalesOrder(updatedOrder);
      enqueueSnackbar('Sales order moved to processing', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReadyToShip = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.updateStatus(salesOrder.id, {
        status: SalesOrderStatus.READY_TO_SHIP,
        notes: 'Order ready to ship'
      });
      setSalesOrder(updatedOrder);
      enqueueSnackbar('Sales order marked as ready to ship', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleShip = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.updateStatus(salesOrder.id, {
        status: SalesOrderStatus.SHIPPED,
        notes: 'Order shipped'
      });
      setSalesOrder(updatedOrder);
      enqueueSnackbar('Sales order marked as shipped', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.cancelSalesOrder(salesOrder.id);
      setSalesOrder(updatedOrder);
      enqueueSnackbar('Sales order cancelled successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to cancel sales order:', error);
      enqueueSnackbar('Failed to cancel sales order', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToInvoice = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const result = await salesOrderService.convertToInvoice(salesOrder.id, dueDays);
      
      enqueueSnackbar(`Invoice ${result.invoice_number} created successfully`, { variant: 'success' });
      setConvertToInvoiceOpen(false);
      
      // Refresh the sales order to show updated status
      const updatedOrder = await salesOrderService.getSalesOrder(salesOrder.id);
      setSalesOrder(updatedOrder);
      
      // Navigate to invoice detail page
      navigate(`/admin/documents/invoices/${result.invoice_id}`);
    } catch (error: any) {
      console.error('Failed to convert to invoice:', error);
      enqueueSnackbar('Failed to convert to invoice', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.updateStatus(salesOrder.id, {
        status: newStatus,
        notes: statusNotes
      });
      setSalesOrder(updatedOrder);
      setStatusUpdateOpen(false);
      setStatusNotes('');
      enqueueSnackbar('Status updated successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateDeliveryChallan = () => {
    // Navigate to delivery challan creation page with sales order ID
    navigate(`/admin/documents/delivery-challans/create?sales_order_id=${salesOrder?.id}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    const errorMessage = typeof error === 'string' 
      ? error 
      : error?.response?.data?.detail 
      ? (Array.isArray(error.response.data.detail) 
          ? error.response.data.detail[0]?.msg || 'An error occurred'
          : error.response.data.detail)
      : error?.message || 'Failed to load sales order';
    
    return (
      <Box>
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    );
  }

  if (!salesOrder) {
    return (
      <Box>
        <Alert severity="error">Sales order not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Link color="inherit" href="/admin/documents/sales-orders">
          Sales Orders
        </Link>
        <Typography color="text.primary">
          {salesOrder.order_number}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/documents/sales-orders')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              Sales Order {salesOrder.order_number}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Chip
                label={salesOrder.status.toUpperCase()}
                color={getStatusColor(salesOrder.status) as any}
                size="small"
                clickable={salesOrder.status === SalesOrderStatus.INVOICED && !!salesOrder.converted_to_invoice_id}
                onClick={salesOrder.status === SalesOrderStatus.INVOICED && salesOrder.converted_to_invoice_id 
                  ? () => navigate(`/admin/documents/invoices/${salesOrder.converted_to_invoice_id}`)
                  : undefined
                }
              />
              {salesOrder.estimate && (
                <Tooltip title="Converted from estimate">
                  <Chip
                    label={`From ${salesOrder.estimate.estimate_number}`}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => navigate(`/admin/documents/estimates/${salesOrder.estimate?.id}`)}
                  />
                </Tooltip>
              )}
              {salesOrder.invoice && (
                <Tooltip title="Converted to invoice">
                  <Chip
                    label={salesOrder.invoice.invoice_number}
                    color="success"
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => navigate(`/admin/documents/invoices/${salesOrder.invoice.id}`)}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ViewIcon />}
            onClick={handleDownloadPDF}
            disabled={actionLoading}
          >
            PDF Preview
          </Button>
          
          {permissions.canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
          )}
          
          {permissions.canConfirm && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ConfirmIcon />}
              onClick={handleConfirm}
              disabled={actionLoading}
            >
              Confirm Order
            </Button>
          )}
          
          {permissions.canProcess && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleProcess}
              disabled={actionLoading}
            >
              Start Processing
            </Button>
          )}
          
          {permissions.canReadyToShip && (
            <Button
              variant="contained"
              color="info"
              onClick={handleReadyToShip}
              disabled={actionLoading}
            >
              Ready to Ship
            </Button>
          )}
          
          {permissions.canShip && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<DeliveryIcon />}
              onClick={handleShip}
              disabled={actionLoading}
            >
              Mark as Shipped
            </Button>
          )}
          
          {permissions.canCreateDeliveryChallan && (
            <Button
              variant="outlined"
              startIcon={<DeliveryIcon />}
              onClick={handleCreateDeliveryChallan}
            >
              Create Delivery Challan
            </Button>
          )}
          
          {permissions.canConvertToInvoice && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<InvoiceIcon />}
              onClick={() => setConvertToInvoiceOpen(true)}
              disabled={actionLoading}
            >
              Convert to Invoice
            </Button>
          )}
          
          <DocumentContextMenu
            documentId={salesOrder.id}
            documentType="sales_order"
            documentNumber={salesOrder.order_number}
            customerEmail={salesOrder.customer?.email}
            customerPhone={salesOrder.customer?.phone}
          />
          
          {permissions.canCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={actionLoading}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Sales Order Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Order Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {salesOrder.order_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Order Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(salesOrder.order_date), 'dd/MM/yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Expected Delivery
                  </Typography>
                  <Typography variant="body1">
                    {salesOrder.expected_delivery_date 
                      ? format(new Date(salesOrder.expected_delivery_date), 'dd/MM/yyyy')
                      : 'Not specified'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={salesOrder.status.toUpperCase()}
                      color={getStatusColor(salesOrder.status) as any}
                      size="small"
                    />
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        if (validStatusTransitions.length > 0) {
                          setNewStatus(validStatusTransitions[0]);
                        }
                        setStatusUpdateOpen(true);
                      }}
                    >
                      Update
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {salesOrder.customer?.billing_name || salesOrder.customer?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Contact: {salesOrder.customer?.contact_person}
                  </Typography>
                  {salesOrder.customer?.email && (
                    <Typography variant="body2" color="text.secondary">
                      Email: {salesOrder.customer.email}
                    </Typography>
                  )}
                  {salesOrder.customer?.phone && (
                    <Typography variant="body2" color="text.secondary">
                      Phone: {salesOrder.customer.phone}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Addresses */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Addresses
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Billing Address
                  </Typography>
                  {salesOrder.billing_address ? (
                    <Box>
                      <Typography variant="body2">
                        {salesOrder.billing_address.address_line1}
                      </Typography>
                      {salesOrder.billing_address.address_line2 && (
                        <Typography variant="body2">
                          {salesOrder.billing_address.address_line2}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        {salesOrder.billing_address.city}, {salesOrder.billing_address.state} {salesOrder.billing_address.postal_code}
                      </Typography>
                      {salesOrder.billing_address.country && (
                        <Typography variant="body2">
                          {salesOrder.billing_address.country}
                        </Typography>
                      )}
                      {salesOrder.billing_address.contact_person && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Contact: {salesOrder.billing_address.contact_person}
                        </Typography>
                      )}
                      {salesOrder.billing_address.contact_phone && (
                        <Typography variant="body2" color="text.secondary">
                          Phone: {salesOrder.billing_address.contact_phone}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No billing address
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Shipping Address
                  </Typography>
                  {salesOrder.shipping_address ? (
                    <Box>
                      <Typography variant="body2">
                        {salesOrder.shipping_address.address_line1}
                      </Typography>
                      {salesOrder.shipping_address.address_line2 && (
                        <Typography variant="body2">
                          {salesOrder.shipping_address.address_line2}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        {salesOrder.shipping_address.city}, {salesOrder.shipping_address.state} {salesOrder.shipping_address.postal_code}
                      </Typography>
                      {salesOrder.shipping_address.country && (
                        <Typography variant="body2">
                          {salesOrder.shipping_address.country}
                        </Typography>
                      )}
                      {salesOrder.shipping_address.contact_person && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Contact: {salesOrder.shipping_address.contact_person}
                        </Typography>
                      )}
                      {salesOrder.shipping_address.contact_phone && (
                        <Typography variant="body2" color="text.secondary">
                          Phone: {salesOrder.shipping_address.contact_phone}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No shipping address
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Line Items
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="right">Line Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesOrder.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {item.variant?.product?.name || 'No Product'} - {item.variant?.name || 'No Variant'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              SKU: {item.variant?.sku || 'N/A'}
                            </Typography>
                            {item.description && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {item.description}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell align="right">
                          {item.discount_percent > 0 ? `${item.discount_percent}%` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(item.line_total || 0)}
                        </TableCell>
                      </TableRow>
                    ))}                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Notes */}
          {salesOrder.notes && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2">
                  {salesOrder.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Summary and Actions */}
        <Grid item xs={12} md={4}>
          {/* Order Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Subtotal:</Typography>
                <Typography>{formatCurrency(salesOrder.subtotal)}</Typography>
              </Box>
              {salesOrder.shipping_charges > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Shipping:</Typography>
                  <Typography>{formatCurrency(salesOrder.shipping_charges)}</Typography>
                </Box>
              )}
              {salesOrder.packing_charges > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Packing:</Typography>
                  <Typography>{formatCurrency(salesOrder.packing_charges)}</Typography>
                </Box>
              )}
              {salesOrder.other_charges.length > 0 && (
                <>
                  {salesOrder.other_charges.map((charge, index) => (
                    <Box key={index} display="flex" justifyContent="space-between" mb={1}>
                      <Typography>{charge.name}:</Typography>
                      <Typography>{formatCurrency(charge.amount)}</Typography>
                    </Box>
                  ))}
                </>
              )}
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Tax:</Typography>
                <Typography>{formatCurrency(salesOrder.tax_amount)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6" color="primary">
                  Total:
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(salesOrder.total_amount)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Order History */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order History
              </Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Created: {format(new Date(salesOrder.created_at), 'dd/MM/yyyy HH:mm')}
                </Typography>
                {salesOrder.created_by_admin && (
                  <Typography variant="body2" color="text.secondary">
                    By: {salesOrder.created_by_admin.name}
                  </Typography>
                )}
                {salesOrder.status_changed_at && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Status changed: {format(new Date(salesOrder.status_changed_at), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                )}
                {salesOrder.invoiced_at && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Invoiced: {format(new Date(salesOrder.invoiced_at), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Shipment Information */}
          {(salesOrder.status === SalesOrderStatus.CONFIRMED || 
            salesOrder.status === SalesOrderStatus.PROCESSING ||
            salesOrder.status === SalesOrderStatus.READY_TO_SHIP ||
            salesOrder.status === SalesOrderStatus.SHIPPED) && (
            <Box sx={{ mt: 2 }}>
              <ShipmentSection
                orderId={salesOrder.id}
                awbNumber={salesOrder.awb_number}
                trackingUrl={salesOrder.tracking_url}
                shipmentStatus={salesOrder.shipment_status}
                carrierName={salesOrder.carrier_name}
                onShipmentCreated={async () => {
                  // Refresh order data after shipment creation
                  const updatedOrder = await salesOrderService.getSalesOrder(salesOrder.id);
                  setSalesOrder(updatedOrder);
                }}
              />
            </Box>
          )}

          {/* PDF Preview */}
          <Box sx={{ mt: 2 }}>
            <SalesOrderPDFPreview
              salesOrder={salesOrder}
              onDownloadPDF={handleDownloadPDF}
            />
          </Box>
        </Grid>

        {/* Audit History */}
        <Grid item xs={12}>
          <DocumentAuditHistory
            documentId={salesOrder.id}
            documentType="sales_order"
            title="Sales Order Audit History"
          />
        </Grid>
      </Grid>

      {/* Convert to Invoice Dialog */}
      <Dialog open={convertToInvoiceOpen} onClose={() => setConvertToInvoiceOpen(false)}>
        <DialogTitle>Convert to Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will create an invoice from this sales order.
          </Typography>
          <TextField
            fullWidth
            label="Due Days"
            type="number"
            value={dueDays}
            onChange={(e) => setDueDays(parseInt(e.target.value) || 30)}
            inputProps={{ min: 1, max: 365 }}
            helperText="Number of days from invoice date for payment due date"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertToInvoiceOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConvertToInvoice}
            disabled={actionLoading}
          >
            {actionLoading ? 'Converting...' : 'Convert to Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusUpdateOpen} onClose={() => setStatusUpdateOpen(false)}>
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          {validStatusTransitions.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No status transitions available from {salesOrder?.status.toUpperCase()}
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Current Status: <strong>{salesOrder?.status.toUpperCase()}</strong>
              </Alert>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>New Status</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as SalesOrderStatus)}
                  label="New Status"
                >
                  {validStatusTransitions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.replace(/_/g, ' ').toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateOpen(false)}>
            Cancel
          </Button>
          {validStatusTransitions.length > 0 && (
            <Button
              variant="contained"
              onClick={handleStatusUpdate}
              disabled={actionLoading}
            >
              {actionLoading ? 'Updating...' : 'Update Status'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesOrderDetail;